import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "../../../assets/css/HeroSection.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function HeroSection() {
  const { section, id } = useParams();
  const [slides, setSlides] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function fetchSlidesData() {
      try {
        await axios.get("sanctum/csrf-cookie");

        const response = await axios.get(
          `${BASE_URL}pagdata?itemName[eq]=slid_show&status02[eq]=publish`,
        );

        if (cancelled) return;

        if (
          response.data.status === 200 &&
          Array.isArray(response.data.allData)
        ) {
          const validSlides = response.data.allData.filter(
            (slide) =>
              slide.item03 ||
              slide.item03b ||
              (slide.item01 &&
                !["undefined", "null"].includes(
                  String(slide.item01).trim().toLowerCase(),
                ) &&
                String(slide.item01).trim() !== ""),
          );

          setSlides(validSlides);
          setActiveIndex(0);
        } else {
          setSlides([]);
        }
      } catch (error) {
        if (!cancelled) {
          setSlides([]);
          console.error("Unable to load hero slides:", error);
        }
      }
    }

    fetchSlidesData();

    return () => {
      cancelled = true;
    };
  }, [section, id]);

  useEffect(() => {
    if (slides.length < 2) return;

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return null;
  }

  const getImageUrl = (filename) => {
    if (
      !filename ||
      ["undefined", "null"].includes(String(filename).trim().toLowerCase())
    ) {
      return "";
    }

    return `${BACKEND_URL}upload/${String(filename).trim()}`;
  };

  const goToSlide = (index) => {
    setActiveIndex((index + slides.length) % slides.length);
  };

  return (
    <section className="fmw-hero" aria-label="Ministry highlights">
      <div className="fmw-hero-carousel">
        {slides.map((slide, index) => {
          const imageUrl = getImageUrl(slide.item01);

          return (
            <article
              key={slide.id ?? `${slide.item01}-${index}`}
              className={`fmw-hero-slide ${
                index === activeIndex ? "is-active" : ""
              }`}
              aria-hidden={index !== activeIndex}
            >
              {imageUrl ? (
                <img
                  className="fmw-hero-image"
                  src={imageUrl}
                  alt={
                    slide.item03 || "Ministry of Housing and Urban Development"
                  }
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "auto"}
                  decoding="async"
                />
              ) : (
                <div className="fmw-hero-image-fallback" />
              )}

              <div className="fmw-hero-shade" />

              <div className="fmw-hero-content">
                <div className="fmw-hero-copy">
                  <span className="fmw-hero-eyebrow">
                    FEDERAL MINISTRY OF WORKS
                  </span>

                  {slide.item03 && (
                    <h1 className="fmw-hero-title">{slide.item03}</h1>
                  )}

                  {slide.item03b && (
                    <p className="fmw-hero-description">{slide.item03b}</p>
                  )}

                  <div className="fmw-hero-accent" />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {slides.length > 1 && (
        <>
          <button
            type="button"
            className="fmw-hero-arrow fmw-hero-prev"
            onClick={() => goToSlide(activeIndex - 1)}
            aria-label="Previous slide"
          >
            <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="fmw-hero-arrow fmw-hero-next"
            onClick={() => goToSlide(activeIndex + 1)}
            aria-label="Next slide"
          >
            <i className="fa-solid fa-arrow-right" aria-hidden="true" />
          </button>

          <div className="fmw-hero-controls">
            <span className="fmw-hero-counter">
              {String(activeIndex + 1).padStart(2, "0")}
              <span className="fmw-hero-counter-divider"> / </span>
              {String(slides.length).padStart(2, "0")}
            </span>

            <div className="fmw-hero-indicators">
              {slides.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`fmw-hero-indicator ${
                    index === activeIndex ? "is-active" : ""
                  }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === activeIndex ? "true" : undefined}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default HeroSection;
